import { useCallback, useLayoutEffect, useRef } from 'react';
import type { Block, CoverBlockContent } from '@/types';
import { uboTheme } from '@/theme/uboTheme';
import PageHeader from './PageHeader';
import { useQuoteClientLogo, useQuotePageBrand, useResolvedAgencyLogoUrl } from '@/store/quoteClientLogoContext';

interface CoverBlockProps {
  block: Block;
  isEditable?: boolean;
  onUpdate?: (patch: Partial<Block>) => void;
}

function BackgroundImage({ src }: { src?: string }) {
  if (!src) return null;
  return (
    <img
      src={src}
      alt=""
      data-full-bleed-artwork
      className="absolute top-0 left-0 w-full h-full object-cover z-[1]"
      onError={(e) => { e.currentTarget.style.display = 'none'; }}
    />
  );
}

function CoverBlock({ block, isEditable = false, onUpdate }: CoverBlockProps) {
  const content = block.content as CoverBlockContent;
  const quoteClientLogo = useQuoteClientLogo();
  const quoteBrand = useQuotePageBrand();
  const theme = content.theme ?? quoteBrand;
  const agencyUrl = useResolvedAgencyLogoUrl(content.agencyLogoUrl, content.theme);
  const titleInputRef = useRef<HTMLTextAreaElement>(null);

  const bgColor = theme === 'ubx' ? uboTheme.colors.ubx.primary : uboTheme.colors.primary;

  const resizeTitle = useCallback((element = titleInputRef.current) => {
    if (!element) return;
    element.style.height = '0px';
    element.style.height = `${element.scrollHeight}px`;
  }, []);

  useLayoutEffect(() => {
    if (!isEditable || !titleInputRef.current) return;

    const element = titleInputRef.current;
    const container = element.parentElement;
    const observer = new ResizeObserver(() => resizeTitle(element));
    if (container) observer.observe(container);

    resizeTitle(element);
    void document.fonts.ready.then(() => resizeTitle(element));

    return () => observer.disconnect();
  }, [content.title, isEditable, resizeTitle]);

  if (isEditable) {
    return (
      <div
        data-full-bleed-surface
        className="w-full h-full flex-1 flex flex-col justify-between relative overflow-hidden text-white"
        style={{ backgroundColor: bgColor, padding: 50 }}
      >
        <BackgroundImage src={content.backgroundUrl} />

        <div className="z-10 flex-shrink-0">
          <PageHeader
            clientLogoUrl={content.clientLogoUrl ?? quoteClientLogo}
            agencyLogoUrl={agencyUrl}
            brand={theme === 'ubx' ? 'ubx' : 'ubo'}
            variant="dark"
            isEditable
            onClientLogoChange={(url) => onUpdate?.({ content: { ...content, clientLogoUrl: url } })}
          />
        </div>

        <div
          className="z-10 mt-auto flex min-w-0 w-full max-w-full flex-shrink flex-col pr-0"
          style={{ maxWidth: 'min(100%, 92%)' }}
        >
          <textarea
            ref={titleInputRef}
            value={content.title || ''}
            onChange={(e) => onUpdate?.({ content: { ...content, title: e.target.value } })}
            onInput={(e) => resizeTitle(e.currentTarget)}
            placeholder="OFFERTE TITEL of {{deal.name}}"
            aria-label="Titel"
            rows={1}
            spellCheck={false}
            className="w-full min-w-0 resize-none overflow-hidden bg-transparent border-none outline-none text-white placeholder:text-white/50"
            style={{
              fontSize: 85,
              fontWeight: 900,
              textTransform: 'uppercase',
              fontFamily: uboTheme.fonts.primary,
              lineHeight: 1.05,
              wordBreak: 'break-word',
              overflowWrap: 'anywhere',
              whiteSpace: 'pre-wrap',
            }}
          />
          <textarea
            value={content.subtitle || ''}
            onChange={(e) => onUpdate?.({ content: { ...content, subtitle: e.target.value } })}
            placeholder="Ondertitel"
            rows={3}
            spellCheck={false}
            className="w-full min-w-0 mt-2 resize-y bg-transparent border-none outline-none text-white placeholder:text-white/50"
            style={{
              fontSize: 35,
              fontWeight: 900,
              textTransform: 'uppercase',
              fontFamily: uboTheme.fonts.primary,
              wordBreak: 'break-word',
              overflowWrap: 'anywhere',
              lineHeight: 1.15,
              minHeight: '3.5rem',
            }}
          />
        </div>

      </div>
    );
  }

  return (
    <div
      data-full-bleed-surface
      className="w-full h-full flex-1 flex flex-col justify-between relative overflow-hidden text-white"
      style={{ backgroundColor: bgColor, padding: 50 }}
    >
      <BackgroundImage src={content.backgroundUrl} />

      <div className="z-10 flex-shrink-0">
        <PageHeader
          clientLogoUrl={content.clientLogoUrl ?? quoteClientLogo}
          agencyLogoUrl={agencyUrl}
          brand={theme === 'ubx' ? 'ubx' : 'ubo'}
          variant="dark"
        />
      </div>

      <div
        className="z-10 mt-auto flex min-w-0 w-full max-w-full flex-shrink flex-col"
        style={{ maxWidth: 'min(100%, 92%)' }}
      >
        <h1
          className="mb-2"
          style={{
            fontSize: 85,
            fontWeight: 900,
            textTransform: 'uppercase',
            fontFamily: uboTheme.fonts.primary,
            lineHeight: 1.05,
            color: 'white',
            wordBreak: 'break-word',
            overflowWrap: 'anywhere',
          }}
        >
          {content.title || 'Offerte Titel'}
        </h1>
        {content.subtitle && (
          <h2
            style={{
              fontSize: 35,
              fontWeight: 900,
              textTransform: 'uppercase',
              fontFamily: uboTheme.fonts.primary,
              color: 'white',
              wordBreak: 'break-word',
              overflowWrap: 'anywhere',
              whiteSpace: 'pre-line',
              lineHeight: 1.15,
            }}
          >
            {content.subtitle}
          </h2>
        )}
      </div>
    </div>
  );
}

export default CoverBlock;
